import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchApplicationFormSharePointSettings,
  invokeApplicationFormSharePointSync,
  saveApplicationFormSharePointSettings,
  testApplicationFormSharePointConnection,
  type ApplicationFormSharePointSettings,
} from '../../../lib/applicationFormSettings';
import { formatSupabaseError } from '../../../lib/formatSupabaseError';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { formatSingaporeDateTime } from '../../../utils/dateTime';
import { Modal } from '../../ui/Modal';
import { SharePointSyncToggle } from '../../settings/SharePointSyncToggle';

function formatSiteInput(row: ApplicationFormSharePointSettings): string {
  if (!row.site_hostname) return '';
  return `https://${row.site_hostname}${row.site_path}`;
}

function parseSiteInput(input: string): { site_hostname: string; site_path: string } {
  const trimmed = input.trim();
  if (!trimmed) return { site_hostname: '', site_path: '' };
  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    return { site_hostname: url.hostname, site_path: url.pathname.replace(/\/$/, '') || '' };
  } catch {
    const parts = trimmed.replace(/^https?:\/\//, '').split('/');
    const host = parts[0] ?? '';
    const path = parts.length > 1 ? `/${parts.slice(1).join('/')}` : '';
    return { site_hostname: host, site_path: path.replace(/\/$/, '') };
  }
}

function formatStoredError(raw?: string | null): string {
  if (!raw) return '—';
  if (raw === '[object Object]') {
    return 'Previous sync failed. Select Sync now to retry.';
  }
  return raw;
}

/** Account Creation workbook sync — independent of CFF SharePoint settings. */
export function ApplicationFormSharePointPanel({
  embeddedInAdminHub = false,
}: {
  embeddedInAdminHub?: boolean;
}) {
  void embeddedInAdminHub;

  const [settings, setSettings] = useState<ApplicationFormSharePointSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editOpenRef = useRef(false);
  editOpenRef.current = editOpen;

  const [enabled, setEnabled] = useState(false);
  const [siteInput, setSiteInput] = useState('');
  const [folderPath, setFolderPath] = useState('CD ACCOUNT RECEIVABLE/01 NEW ACCOUNT CREATION FORM');
  const [workbookName, setWorkbookName] = useState('Account Creation Submissions.xlsx');
  const [sheetName, setSheetName] = useState('Submissions');

  const applyRow = useCallback((row: ApplicationFormSharePointSettings) => {
    setEnabled(row.enabled);
    if (!editOpenRef.current) {
      setSiteInput(formatSiteInput(row));
      setFolderPath(row.folder_path || 'CD ACCOUNT RECEIVABLE/01 NEW ACCOUNT CREATION FORM');
      setWorkbookName(row.workbook_name || 'Account Creation Submissions.xlsx');
      setSheetName(row.sheet_name || 'Submissions');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await fetchApplicationFormSharePointSettings();
      setSettings(row);
      if (row) applyRow(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : formatSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }, [applyRow]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['sharepoint_settings'],
    onChange: () => void load(),
  });

  function openEdit() {
    if (settings) {
      setEnabled(settings.enabled);
      setSiteInput(formatSiteInput(settings));
      setFolderPath(settings.folder_path || 'CD ACCOUNT RECEIVABLE/01 NEW ACCOUNT CREATION FORM');
      setWorkbookName(settings.workbook_name || 'Account Creation Submissions.xlsx');
      setSheetName(settings.sheet_name || 'Submissions');
    }
    setError(null);
    setMessage(null);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    if (settings) applyRow(settings);
    setError(null);
    setMessage(null);
  }

  async function handleToggleEnabled(next: boolean) {
    if (!settings?.site_hostname) {
      setError('Set site URL and folder in Edit before enabling sync.');
      return;
    }
    setToggling(true);
    setError(null);
    setMessage(null);
    setEnabled(next);
    try {
      const saved = await saveApplicationFormSharePointSettings({
        enabled: next,
        site_hostname: settings.site_hostname,
        site_path: settings.site_path || '',
        folder_path: settings.folder_path || 'CD ACCOUNT RECEIVABLE/01 NEW ACCOUNT CREATION FORM',
        workbook_name: settings.workbook_name || 'Account Creation Submissions.xlsx',
        sheet_name: settings.sheet_name || 'Submissions',
      });
      setSettings(saved);
      setMessage(next ? 'Sync turned on.' : 'Sync turned off.');
    } catch (err) {
      setEnabled(!next);
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setToggling(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const parsed = parseSiteInput(siteInput);
    if (!parsed.site_hostname || !parsed.site_path) {
      setError('Enter a valid SharePoint site URL (e.g. https://banteckhansg.sharepoint.com/sites/BanTeckHan).');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveApplicationFormSharePointSettings({
        enabled,
        site_hostname: parsed.site_hostname,
        site_path: parsed.site_path,
        folder_path: folderPath.trim(),
        workbook_name: workbookName.trim(),
        sheet_name: sheetName.trim(),
      });
      setSettings(saved);
      setMessage('Account Creation SharePoint settings saved.');
      setEditOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncNow() {
    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      const result = await invokeApplicationFormSharePointSync();
      if (result?.disabled) {
        setMessage('Sync is disabled. Turn sync on to push Account Creation submissions to SharePoint.');
      } else {
        setMessage(
          `Synced ${String(result.row_count ?? 0)} row(s) to ${
            settings?.workbook_name || workbookName.trim() || 'workbook'
          }.`,
        );
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed.');
      await load();
    } finally {
      setSyncing(false);
    }
  }

  async function handleTest() {
    const source = editOpen
      ? { site: siteInput, folder: folderPath }
      : {
          site: settings ? formatSiteInput(settings) : siteInput,
          folder: settings?.folder_path || folderPath,
        };
    const parsed = parseSiteInput(source.site);
    if (!parsed.site_hostname || !parsed.site_path) {
      setError('Enter a valid SharePoint site URL before testing.');
      return;
    }
    if (!source.folder.trim()) {
      setError('Enter a folder path before testing.');
      return;
    }

    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await testApplicationFormSharePointConnection({
        target: 'account-creation',
        site_hostname: parsed.site_hostname,
        site_path: parsed.site_path,
        folder_path: source.folder.trim(),
      });
      setMessage(
        `Connected to ${String(result.siteName ?? 'SharePoint site')}${
          result.folderUrl ? ' · folder OK' : ''
        }.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  }

  const busy = loading || saving || syncing || testing || toggling;
  const siteDisplay = settings ? formatSiteInput(settings) : '';

  return (
    <>
      <article className="sharepoint-config-row">
        <div className="sharepoint-config-row__main">
          <div className="sharepoint-config-row__identity">
            <h3 className="sharepoint-config-row__title">Account Creation</h3>
            {!loading ? (
              <p className="sharepoint-config-row__meta" title={siteDisplay || undefined}>
                {siteDisplay || 'No site configured'}
                {settings?.folder_path ? ` · ${settings.folder_path}` : ''}
              </p>
            ) : (
              <p className="sharepoint-config-row__meta">Loading…</p>
            )}
          </div>

          <div className="sharepoint-config-row__status">
            <div>
              <span className="sharepoint-config-row__status-label">Last sync</span>
              <span className="sharepoint-config-row__status-value">
                {settings?.last_sync_at ? formatSingaporeDateTime(settings.last_sync_at) : '—'}
              </span>
            </div>
            <div className="sharepoint-config-row__status-wide">
              <span className="sharepoint-config-row__status-label">
                {settings?.last_sync_error && !editOpen ? 'Error' : 'File'}
              </span>
              {settings?.last_sync_error && !editOpen ? (
                <span
                  className="sharepoint-config-row__status-value sharepoint-config-row__status-value--error"
                  title={formatStoredError(settings.last_sync_error)}
                >
                  {formatStoredError(settings.last_sync_error)}
                </span>
              ) : (
                <span
                  className="sharepoint-config-row__status-value"
                  title={settings?.workbook_name ?? undefined}
                >
                  {settings?.workbook_name || '—'}
                </span>
              )}
            </div>
          </div>
        </div>

        {!editOpen && error ? <div className="alert alert-error sharepoint-config-row__alert">{error}</div> : null}
        {!editOpen && message ? (
          <div className="alert alert-success sharepoint-config-row__alert">{message}</div>
        ) : null}

        <div className="sharepoint-config-row__actions">
          <SharePointSyncToggle
            variant="compact"
            checked={enabled}
            disabled={busy}
            onChange={(next) => void handleToggleEnabled(next)}
          />
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleTest()}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleSyncNow()}
          >
            {syncing ? 'Working…' : 'Sync now'}
          </button>
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={openEdit}>
            Edit
          </button>
        </div>
      </article>

      <Modal title="Edit · Account Creation" open={editOpen} onClose={closeEdit} wide>
        <form
          className="settings-sharepoint-form settings-sharepoint-form--modal"
          onSubmit={(e) => void handleSave(e)}
        >
          {error ? <div className="alert alert-error">{error}</div> : null}
          {message ? <div className="alert alert-success">{message}</div> : null}

          <div className="settings-sharepoint-form__fields settings-sharepoint-form__fields--2x2">
            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Site URL</span>
              <input
                className="field-input"
                placeholder="https://banteckhansg.sharepoint.com/sites/BanTeckHan"
                value={siteInput}
                onChange={(e) => setSiteInput(e.target.value)}
                disabled={saving}
              />
            </label>

            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Folder path</span>
              <input
                className="field-input"
                placeholder="CD ACCOUNT RECEIVABLE/01 NEW ACCOUNT CREATION FORM"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                disabled={saving}
              />
            </label>

            <label className="field">
              <span className="field-label">Workbook file</span>
              <input
                className="field-input"
                placeholder="Account Creation Submissions.xlsx"
                value={workbookName}
                onChange={(e) => setWorkbookName(e.target.value)}
                disabled={saving}
              />
            </label>

            <label className="field">
              <span className="field-label">Sheet name</span>
              <input
                className="field-input"
                placeholder="Submissions"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                disabled={saving}
              />
            </label>
          </div>

          <div className="settings-sharepoint-form__actions">
            <button type="submit" className="btn-primary" disabled={saving || testing}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || testing}
              onClick={() => void handleTest()}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button type="button" className="btn-secondary" disabled={saving} onClick={closeEdit}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
